'use client';

import { useState, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import NewsletterPopup from './NewsletterPopup';

const STORAGE_KEY = 'nl_overlay_dismissed';
const SUBSCRIBED_KEY = 'nl_subscribed';
const COOLDOWN_DAYS = 7;
const DELAY_MS = 30_000;
const SCROLL_THRESHOLD = 0.5;

export default function NewsletterOverlay() {
  const [show, setShow] = useState(false);
  const pathname = usePathname();

  const shouldSuppress = useCallback(() => {
    if (pathname?.startsWith('/admin')) return true;

    try {
      if (localStorage.getItem(SUBSCRIBED_KEY)) return true;

      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed) {
        const elapsed = Date.now() - parseInt(dismissed, 10);
        if (elapsed < COOLDOWN_DAYS * 86400000) return true;
      }
    } catch {
      return false;
    }

    return false;
  }, [pathname]);

  useEffect(() => {
    if (shouldSuppress()) return;

    let triggered = false;

    const timer = setTimeout(() => {
      if (!triggered && !shouldSuppress()) {
        triggered = true;
        setShow(true);
      }
    }, DELAY_MS);

    function onScroll() {
      if (triggered) return;
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPercent >= SCROLL_THRESHOLD && !shouldSuppress()) {
        triggered = true;
        setShow(true);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [shouldSuppress]);

  function handleClose() {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
  }

  return (
    <NewsletterPopup
      open={show}
      onClose={handleClose}
      onSubscribed={() => {
        try {
          localStorage.setItem(SUBSCRIBED_KEY, '1');
        } catch {}
      }}
    />
  );
}
